/* eslint-disable no-unused-vars */
// =============================================================================
// AddCampaignAssetScreen.js — FULL REBUILD
// JobEng — Business / Marketing
// Location: app/screens/business/marketing/AddCampaignAssetScreen.js
//
// Live DB: POST /api/data/marketing_assets · base64 image upload via existing
//          photo endpoint · caches marketing_assets_<id> + campaign_content_<id>
// DB connection monitor: /health + marketing_assets table ping badge
// FIXED: AI generation now uses the server-side /api/ai Groq proxy
//        (the old version called api.anthropic.com directly from the app with
//        no key — guaranteed to fail in production and leaks architecture)
// Features: 6 asset types · camera/gallery/document pickers · file size
//   validation per type · auto-dimensions · platform targeting with live
//   character counters per platform · tags · status incl. scheduled date ·
//   AI content with quick prompts + "use as" actions · live preview card ·
//   upload progress · Add Another / Done flow
// =============================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  TextInput,
  Image,
  ActivityIndicator,
  Alert,
  Modal,
  Platform,
  KeyboardAvoidingView,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import * as DocumentPicker from 'expo-document-picker';
import AsyncStorage from '@react-native-async-storage/async-storage';

// ─── API ─────────────────────────────────────────────────────────────────────

const API_BASE = 'https://api.jobeng.ai';
const API_KEY =
  '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const getH = async () => {
  const tok =
    (await AsyncStorage.getItem('auth_token').catch(() => null)) ||
    (await AsyncStorage.getItem('userToken').catch(() => null)) ||
    (await AsyncStorage.getItem('token').catch(() => null));
  return {
    'Content-Type': 'application/json',
    'x-api-key': API_KEY,
    ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
  };
};

const getBid = async () =>
  parseInt(await AsyncStorage.getItem('company_id').catch(() => '0'), 10) || 0;

// ─── Constants ───────────────────────────────────────────────────────────────

const C = {
  primary: '#0e4e96',
  primaryDark: '#093a72',
  primaryLight: '#1e7be2',
  accent: '#EC4899',
  bg: '#F4F6FB',
  card: '#FFFFFF',
  text: '#0F172A',
  sub: '#64748B',
  border: '#E2E8F0',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
  purple: '#8b5cf6',
  teal: '#06b6d4',
};

const ASSET_TYPES = [
  { id: 'image', name: 'Image', icon: 'image-outline', color: C.primary, maxMB: 10, formats: 'JPG, PNG, GIF, WebP', dims: '1200×628 px (1.91:1)' },
  { id: 'video', name: 'Video', icon: 'video-outline', color: '#ef4444', maxMB: 500, formats: 'MP4, MOV, AVI', dims: '1920×1080 (16:9)' },
  { id: 'document', name: 'Document', icon: 'file-document-outline', color: C.warning, maxMB: 50, formats: 'PDF, DOCX, PPTX, XLSX', dims: 'Any' },
  { id: 'link', name: 'Link / URL', icon: 'link-variant', color: C.success, maxMB: null, formats: 'URL', dims: null },
  { id: 'social_post', name: 'Social Post', icon: 'share-variant', color: C.purple, maxMB: 5, formats: 'Text + Image', dims: 'Platform specific' },
  { id: 'ai_content', name: 'AI Content', icon: 'robot-outline', color: C.teal, maxMB: null, formats: 'Text, Prompts', dims: null },
];

const PLATFORMS = [
  { id: 'LinkedIn', icon: 'linkedin', color: '#0077B5', charLimit: 3000 },
  { id: 'Twitter', icon: 'twitter', color: '#1DA1F2', charLimit: 280 },
  { id: 'Facebook', icon: 'facebook', color: '#1877F2', charLimit: 63206 },
  { id: 'Instagram', icon: 'instagram', color: '#E1306C', charLimit: 2200 },
  { id: 'Email', icon: 'email-outline', color: C.primary, charLimit: null },
  { id: 'Job Boards', icon: 'briefcase-outline', color: '#28a745', charLimit: null },
  { id: 'Website', icon: 'web', color: '#6c757d', charLimit: null },
];

const STATUS_OPTIONS = ['draft', 'active', 'scheduled', 'archived'];

const AI_QUICK_PROMPTS = [
  'Write a LinkedIn post promoting this campaign',
  'Write a short job advert hook',
  'Write 5 social media captions with hashtags',
  'Write a promotional email opening paragraph',
];

const fmtSize = (bytes) => {
  if (!bytes || bytes === 0) return 'Unknown size';
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
};

const isValidUrl = (url) => { try { new URL(url); return true; } catch { return false; } };
const isValidDate = (d) => /^\d{4}-\d{2}-\d{2}$/.test(d);

// =============================================================================
// MODULE-SCOPE COMPONENTS (React.memo)
// =============================================================================

const DbBadge = React.memo(({ status, onPress }) => {
  const cfg =
    status === 'online' ? { c: '#34D399', t: 'Live' }
    : status === 'offline' ? { c: '#F87171', t: 'Offline' }
    : { c: '#FBBF24', t: '…' };
  return (
    <TouchableOpacity style={s.dbBadge} onPress={onPress} activeOpacity={0.7}>
      <View style={[s.dbDot, { backgroundColor: cfg.c }]} />
      <Text style={s.dbText}>{cfg.t}</Text>
    </TouchableOpacity>
  );
});

const TypeCard = React.memo(({ type, active, onPress }) => (
  <TouchableOpacity
    style={[s.typeCard, active && { backgroundColor: type.color, borderColor: type.color }]}
    onPress={() => onPress(type.id)}
    activeOpacity={0.7}
  >
    <MaterialCommunityIcons name={type.icon} size={22} color={active ? '#fff' : type.color} />
    <Text style={[s.typeName, active && { color: '#fff' }]}>{type.name}</Text>
  </TouchableOpacity>
));

const SectionTitle = React.memo(({ children, right }) => (
  <View style={s.sectionTitleRow}>
    <Text style={s.sectionTitle}>{children}</Text>
    {right || null}
  </View>
));

const Field = React.memo(({ label, value, onChangeText, placeholder, multiline, keyboardType, maxLength, counter }) => (
  <View style={s.fieldWrap}>
    <View style={s.fieldLabelRow}>
      <Text style={s.fieldLabel}>{label}</Text>
      {counter ? <Text style={s.fieldCounter}>{counter}</Text> : null}
    </View>
    <TextInput
      style={[s.input, multiline && s.inputMultiline]}
      value={value}
      onChangeText={onChangeText}
      placeholder={placeholder}
      placeholderTextColor="#9CA3AF"
      multiline={!!multiline}
      keyboardType={keyboardType || 'default'}
      maxLength={maxLength}
    />
  </View>
));

const PlatformChip = React.memo(({ platform, active, onPress }) => (
  <TouchableOpacity
    style={[s.platformChip, active && { backgroundColor: platform.color, borderColor: platform.color }]}
    onPress={() => onPress(platform.id)}
    activeOpacity={0.7}
  >
    <MaterialCommunityIcons name={platform.icon} size={14} color={active ? '#fff' : platform.color} />
    <Text style={[s.platformText, active && { color: '#fff' }]}>{platform.id}</Text>
  </TouchableOpacity>
));

const Chip = React.memo(({ label, active, onPress }) => (
  <TouchableOpacity style={[s.chip, active && s.chipActive]} onPress={onPress} activeOpacity={0.7}>
    <Text style={[s.chipText, active && s.chipTextActive]}>{label}</Text>
  </TouchableOpacity>
));

const TagPill = React.memo(({ tag, onRemove }) => (
  <View style={s.tagPill}>
    <Text style={s.tagText}>#{tag}</Text>
    <TouchableOpacity onPress={() => onRemove(tag)} hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}>
      <Ionicons name="close" size={13} color={C.primary} />
    </TouchableOpacity>
  </View>
));

// =============================================================================
// MAIN SCREEN
// =============================================================================

export default function AddCampaignAssetScreen({ navigation, route }) {
  const { campaignId, campaign } = route?.params || {};

  // Core fields
  const [assetType, setAssetType] = useState('image');
  const [assetName, setAssetName] = useState('');
  const [assetDescription, setAssetDescription] = useState('');
  const [status, setStatus] = useState('active');
  const [scheduledDate, setScheduledDate] = useState('');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [targetPlatforms, setTargetPlatforms] = useState([]);

  // File types
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUri, setPreviewUri] = useState(null);
  const [altText, setAltText] = useState('');
  const [dimensions, setDimensions] = useState('');
  const [videoDuration, setVideoDuration] = useState('');

  // Link type
  const [linkUrl, setLinkUrl] = useState('');
  const [linkTitle, setLinkTitle] = useState('');
  const [linkCtaText, setLinkCtaText] = useState('');

  // Social post type
  const [postText, setPostText] = useState('');

  // AI content type
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiContent, setAiContent] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);

  // Upload
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // DB
  const [dbStatus, setDbStatus] = useState('checking');
  const [dbDetail, setDbDetail] = useState('');

  const currentType = ASSET_TYPES.find((t) => t.id === assetType) || ASSET_TYPES[0];

  // ---------------------------------------------------------------------------
  // DB CHECK
  // ---------------------------------------------------------------------------

  const checkDb = useCallback(async () => {
    setDbStatus('checking');
    try {
      const h = await getH();
      const t0 = Date.now();
      const health = await fetch(`${API_BASE}/health`, { headers: h }).then((r) => r.ok).catch(() => false);
      const table = await fetch(`${API_BASE}/api/data/marketing_assets?limit=1`, { headers: h }).then((r) => r.ok).catch(() => false);
      const ms = Date.now() - t0;
      if (health && table) { setDbStatus('online'); setDbDetail(`API + marketing_assets table reachable · ${ms}ms`); }
      else if (health) { setDbStatus('offline'); setDbDetail('API up, marketing_assets table unreachable — the table may not exist yet'); }
      else { setDbStatus('offline'); setDbDetail('API not responding — check PM2 / nginx'); }
    } catch (e) { setDbStatus('offline'); setDbDetail(e?.message || 'Network error'); }
  }, []);

  useEffect(() => { checkDb(); }, [checkDb]);

  const showDbDetail = useCallback(() => {
    Alert.alert(
      dbStatus === 'online' ? '✅ Connected' : '⚠️ Connection issue',
      dbDetail || 'Checking…',
      [{ text: 'Re-check', onPress: checkDb }, { text: 'OK' }]
    );
  }, [dbStatus, dbDetail, checkDb]);

  // ---------------------------------------------------------------------------
  // PICKERS
  // ---------------------------------------------------------------------------

  const validateSize = useCallback((bytes) => {
    if (!currentType.maxMB || !bytes) return true;
    const mb = bytes / (1024 * 1024);
    if (mb > currentType.maxMB) {
      Alert.alert('File too large', `${currentType.name} files must be under ${currentType.maxMB} MB (this one is ${mb.toFixed(1)} MB).`);
      return false;
    }
    return true;
  }, [currentType]);

  const pickImage = useCallback(async () => {
    const { status: perm } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (perm !== 'granted') { Alert.alert('Permission required', 'Please allow photo library access.'); return; }
    const r = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images, quality: 0.8, base64: true,
    });
    if (!r.canceled && r.assets?.[0]) {
      const a = r.assets[0];
      const file = {
        uri: a.uri, base64: a.base64,
        name: a.fileName || `image_${Date.now()}.jpg`,
        size: a.fileSize || (a.base64 ? a.base64.length * 0.75 : 0),
        mimeType: a.mimeType || 'image/jpeg',
      };
      if (!validateSize(file.size)) return;
      setSelectedFile(file);
      setPreviewUri(a.uri);
      if (a.width && a.height) setDimensions(`${a.width}×${a.height}`);
      if (!assetName) setAssetName(file.name.split('.')[0].replace(/[-_]/g, ' '));
    }
  }, [assetName, validateSize]);

  const capturePhoto = useCallback(async () => {
    const { status: perm } = await ImagePicker.requestCameraPermissionsAsync();
    if (perm !== 'granted') { Alert.alert('Permission required', 'Please allow camera access.'); return; }
    const r = await ImagePicker.launchCameraAsync({ quality: 0.8, base64: true });
    if (!r.canceled && r.assets?.[0]) {
      const a = r.assets[0];
      const file = { uri: a.uri, base64: a.base64, name: `camera_${Date.now()}.jpg`, size: a.fileSize || 0, mimeType: 'image/jpeg' };
      if (!validateSize(file.size)) return;
      setSelectedFile(file);
      setPreviewUri(a.uri);
      if (a.width && a.height) setDimensions(`${a.width}×${a.height}`);
      if (!assetName) setAssetName('Camera photo');
    }
  }, [assetName, validateSize]);

  const pickVideo = useCallback(async () => {
    const { status: perm } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (perm !== 'granted') { Alert.alert('Permission required', 'Please allow photo library access.'); return; }
    const r = await ImagePicker.launchImageLibraryAsync({ mediaTypes: ImagePicker.MediaTypeOptions.Videos, quality: 0.8 });
    if (!r.canceled && r.assets?.[0]) {
      const a = r.assets[0];
      const file = { uri: a.uri, name: a.fileName || `video_${Date.now()}.mp4`, size: a.fileSize || 0, mimeType: a.mimeType || 'video/mp4' };
      if (!validateSize(file.size)) return;
      setSelectedFile(file);
      setPreviewUri(a.uri);
      if (a.duration) {
        const sec = Math.floor((a.duration > 10000 ? a.duration / 1000 : a.duration));
        setVideoDuration(`${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`);
      }
      if (a.width && a.height) setDimensions(`${a.width}×${a.height}`);
      if (!assetName) setAssetName(file.name.split('.')[0].replace(/[-_]/g, ' '));
    }
  }, [assetName, validateSize]);

  const pickDocument = useCallback(async () => {
    const r = await DocumentPicker.getDocumentAsync({ type: '*/*', copyToCacheDirectory: true });
    const asset = r.assets?.[0] || (r.type === 'success' ? r : null);
    if (asset) {
      const file = { uri: asset.uri, name: asset.name, size: asset.size || 0, mimeType: asset.mimeType || 'application/octet-stream' };
      if (!validateSize(file.size)) return;
      setSelectedFile(file);
      setPreviewUri(null);
      if (!assetName) setAssetName(asset.name.split('.')[0].replace(/[-_]/g, ' '));
    }
  }, [assetName, validateSize]);

  const showImageOptions = useCallback(() => {
    Alert.alert('Add image', 'Choose source', [
      { text: 'Camera', onPress: capturePhoto },
      { text: 'Gallery', onPress: pickImage },
      { text: 'Cancel', style: 'cancel' },
    ]);
  }, [capturePhoto, pickImage]);

  const handleFilePicker = useCallback(() => {
    switch (assetType) {
      case 'image': showImageOptions(); break;
      case 'video': pickVideo(); break;
      case 'document': pickDocument(); break;
      case 'social_post': showImageOptions(); break;
      default: showImageOptions();
    }
  }, [assetType, showImageOptions, pickVideo, pickDocument]);

  // ---------------------------------------------------------------------------
  // TAGS & PLATFORMS
  // ---------------------------------------------------------------------------

  const addTag = useCallback(() => {
    const t = tagInput.trim().toLowerCase().replace(/^#/, '');
    if (t && !tags.includes(t)) setTags((prev) => [...prev, t]);
    setTagInput('');
  }, [tagInput, tags]);

  const removeTag = useCallback((t) => setTags((prev) => prev.filter((x) => x !== t)), []);

  const togglePlatform = useCallback((id) =>
    setTargetPlatforms((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id])), []);

  // Tightest character limit among selected platforms (for social posts)
  const charLimit = useMemo(() => {
    const limits = targetPlatforms
      .map((id) => PLATFORMS.find((p) => p.id === id)?.charLimit)
      .filter((x) => typeof x === 'number');
    return limits.length ? Math.min(...limits) : null;
  }, [targetPlatforms]);

  // ---------------------------------------------------------------------------
  // AI — via server-side /api/ai proxy (FIXED: was a keyless direct
  // api.anthropic.com call that always failed in production)
  // ---------------------------------------------------------------------------

  const generateAiContent = useCallback(async (promptOverride) => {
    const userPrompt = (promptOverride ?? aiPrompt).trim();
    if (!userPrompt) { Alert.alert('Required', 'Enter a prompt first.'); return; }
    if (promptOverride) setAiPrompt(promptOverride);
    setAiGenerating(true);
    try {
      const h = await getH();
      const prompt =
        `You are a UK marketing copywriter for a business on the JobEng platform.` +
        (campaign?.name ? ` Campaign context: "${campaign.name}"${campaign.objective ? ` (objective: ${campaign.objective})` : ''}.` : '') +
        ` British English, plain text, no markdown symbols. Task: ${userPrompt}`;
      const res = await fetch(`${API_BASE}/api/ai`, {
        method: 'POST', headers: h, body: JSON.stringify({ prompt }),
      }).then((r) => r.json()).catch(() => null);
      if (!res?.content) throw new Error(res?.message || 'No response from AI service');
      setAiContent(String(res.content).trim());
      if (!assetName) setAssetName(userPrompt.slice(0, 50));
    } catch (e) {
      Alert.alert('AI unavailable', e?.message || 'Could not generate content.');
    } finally { setAiGenerating(false); }
  }, [aiPrompt, campaign, assetName]);

  // ---------------------------------------------------------------------------
  // VALIDATION
  // ---------------------------------------------------------------------------

  const validate = useCallback(() => {
    if (!assetName.trim()) { Alert.alert('Required', 'Please enter an asset name.'); return false; }
    if (status === 'scheduled' && scheduledDate && !isValidDate(scheduledDate)) {
      Alert.alert('Invalid date', 'Scheduled date must be YYYY-MM-DD.'); return false;
    }
    if (assetType === 'link') {
      if (!linkUrl.trim() || !isValidUrl(linkUrl.trim())) { Alert.alert('Required', 'Enter a valid URL (https://…).'); return false; }
    } else if (assetType === 'ai_content') {
      if (!aiContent.trim()) { Alert.alert('Required', 'Generate or paste the AI content first.'); return false; }
    } else if (assetType === 'social_post') {
      if (!postText.trim()) { Alert.alert('Required', 'Enter the post text.'); return false; }
      if (charLimit && postText.length > charLimit) {
        Alert.alert('Too long', `Post exceeds the ${charLimit}-character limit of a selected platform.`); return false;
      }
    } else if (!selectedFile) {
      Alert.alert('Required', 'Please select a file to upload.'); return false;
    }
    return true;
  }, [assetName, status, scheduledDate, assetType, linkUrl, aiContent, postText, charLimit, selectedFile]);

  // ---------------------------------------------------------------------------
  // SAVE
  // ---------------------------------------------------------------------------

  const resetForm = useCallback(() => {
    setAssetName(''); setAssetDescription(''); setStatus('active'); setScheduledDate('');
    setTags([]); setTagInput(''); setTargetPlatforms([]);
    setSelectedFile(null); setPreviewUri(null); setAltText(''); setDimensions(''); setVideoDuration('');
    setLinkUrl(''); setLinkTitle(''); setLinkCtaText('');
    setPostText(''); setAiPrompt(''); setAiContent('');
    setUploadProgress(0);
  }, []);

  const handleSave = useCallback(async () => {
    if (!validate()) return;
    const bid = await getBid();
    if (!bid) { Alert.alert('No company', 'Company context missing — sign in again.'); return; }
    const userId = parseInt(await AsyncStorage.getItem('user_id').catch(() => '0'), 10) || 0;

    setUploading(true);
    setUploadProgress(10);

    try {
      const h = await getH();

      // Resolve file URL
      let fileUrl = null;
      if (selectedFile?.base64) {
        const photoRes = await fetch(`${API_BASE}/api/employees/0/photo`, {
          method: 'POST', headers: h,
          body: JSON.stringify({
            image_data: `data:${selectedFile.mimeType || 'image/jpeg'};base64,${selectedFile.base64}`,
            image_type: 'jpeg',
          }),
        }).catch(() => null);
        setUploadProgress(45);
        if (photoRes?.ok) {
          const pj = await photoRes.json().catch(() => ({}));
          fileUrl = pj?.url || pj?.data?.url || selectedFile.uri;
        } else { fileUrl = selectedFile.uri; }
      } else if (assetType === 'link') {
        fileUrl = linkUrl.trim();
      } else if (assetType === 'ai_content' || assetType === 'social_post') {
        fileUrl = previewUri || null;
      } else {
        fileUrl = selectedFile?.uri || null;
      }

      setUploadProgress(60);

      const payload = {
        company_id: bid,
        business_id: bid,            // trigger sync_company_business_id keeps these aligned
        created_by: userId,          // NOT NULL column
        campaign_id: campaignId || null,
        asset_type: assetType.slice(0, 50),
        asset_name: assetName.trim().slice(0, 200),
        asset_category: 'campaign',
        description: assetDescription.trim() || null,
        status,
        scheduled_date: status === 'scheduled' && scheduledDate ? scheduledDate : null,
        tags: JSON.stringify(tags),
        target_platforms: JSON.stringify(targetPlatforms),
        url: fileUrl ? String(fileUrl).slice(0, 500) : null,
        file_path: selectedFile?.name ? String(selectedFile.name).slice(0, 500) : null,
        file_size_bytes: selectedFile?.size || null,
        mime_type: selectedFile?.mimeType ? String(selectedFile.mimeType).slice(0, 100) : null,
        alt_text: altText || null,
        dimensions: dimensions ? String(dimensions).slice(0, 50) : null,
        video_duration: videoDuration ? String(videoDuration).slice(0, 20) : null,
        link_url: linkUrl || null,
        link_title: linkTitle || null,
        link_cta: linkCtaText || null,
        post_text: postText || null,
        ai_prompt: aiPrompt || null,
        ai_content: aiContent || null,
      };

      const res = await fetch(`${API_BASE}/api/data/marketing_assets`, {
        method: 'POST', headers: h, body: JSON.stringify(payload),
      });
      const json = await res.json().catch(() => ({}));
      const savedToServer = res.ok && json?.success !== false;
      setUploadProgress(85);

      const savedAsset = json?.data || { ...payload, id: `asset_${Date.now()}` };

      // Caches (kept compatible with the rest of the marketing screens)
      const cacheKey = `marketing_assets_${campaignId || 'all'}`;
      const existing = JSON.parse((await AsyncStorage.getItem(cacheKey).catch(() => '[]')) || '[]');
      await AsyncStorage.setItem(cacheKey, JSON.stringify([savedAsset, ...existing].slice(0, 100)));

      if (campaignId) {
        const libKey = `campaign_content_${campaignId}`;
        const libArr = JSON.parse((await AsyncStorage.getItem(libKey).catch(() => '[]')) || '[]');
        libArr.unshift({ type: assetType, title: assetName, status, id: savedAsset.id, created_at: new Date().toISOString() });
        await AsyncStorage.setItem(libKey, JSON.stringify(libArr.slice(0, 50)));
      }

      setUploadProgress(100);
      setTimeout(() => {
        setUploading(false);
        Alert.alert(
          savedToServer ? '✅ Asset saved' : '⚠️ Saved locally',
          savedToServer
            ? `"${assetName}" has been added to your campaign assets.`
            : `Server unavailable — "${assetName}" was cached on this device and will appear in the asset list.`,
          [
            { text: 'Add another', onPress: resetForm },
            { text: 'Done', onPress: () => navigation.goBack() },
          ]
        );
      }, 350);
    } catch (e) {
      setUploading(false);
      Alert.alert('Save failed', e?.message || 'Could not save the asset.');
    }
  }, [validate, selectedFile, assetType, linkUrl, previewUri, campaignId, assetName, assetDescription,
      status, scheduledDate, tags, targetPlatforms, altText, dimensions, videoDuration, linkTitle,
      linkCtaText, postText, aiPrompt, aiContent, resetForm, navigation]);

  // ---------------------------------------------------------------------------
  // TYPE-SPECIFIC SECTIONS
  // ---------------------------------------------------------------------------

  const renderFileSection = () => (
    <View>
      <SectionTitle>{currentType.name} file</SectionTitle>
      <View style={s.specRow}>
        <Text style={s.specText}>Formats: {currentType.formats}</Text>
        {currentType.maxMB && <Text style={s.specText}>Max {currentType.maxMB} MB</Text>}
        {currentType.dims && <Text style={s.specText}>Best: {currentType.dims}</Text>}
      </View>

      {selectedFile ? (
        <View style={s.filePreview}>
          {previewUri ? (
            <Image source={{ uri: previewUri }} style={s.previewImg} resizeMode="cover" onError={() => {}} />
          ) : (
            <View style={[s.previewImg, s.previewDoc]}>
              <MaterialCommunityIcons name={currentType.icon} size={32} color={currentType.color} />
            </View>
          )}
          <View style={{ flex: 1, marginLeft: 12 }}>
            <Text style={s.fileName} numberOfLines={1}>{selectedFile.name}</Text>
            <Text style={s.fileMeta}>{fmtSize(selectedFile.size)}{dimensions ? ` · ${dimensions}` : ''}{videoDuration ? ` · ${videoDuration}` : ''}</Text>
            <TouchableOpacity onPress={handleFilePicker}>
              <Text style={s.changeFile}>Change file</Text>
            </TouchableOpacity>
          </View>
          <TouchableOpacity onPress={() => { setSelectedFile(null); setPreviewUri(null); setDimensions(''); setVideoDuration(''); }}>
            <Ionicons name="close-circle" size={22} color={C.danger} />
          </TouchableOpacity>
        </View>
      ) : (
        <TouchableOpacity style={s.dropZone} onPress={handleFilePicker} activeOpacity={0.7}>
          <MaterialCommunityIcons name={currentType.icon} size={34} color={currentType.color} />
          <Text style={s.dropTitle}>Tap to select {currentType.name.toLowerCase()}</Text>
          <Text style={s.dropSub}>{assetType === 'image' || assetType === 'social_post' ? 'Camera or gallery' : assetType === 'video' ? 'From your library' : 'Browse files'}</Text>
        </TouchableOpacity>
      )}

      {(assetType === 'image' || assetType === 'social_post') && (
        <Field label="Alt text (accessibility)" value={altText} onChangeText={setAltText} placeholder="Describe the image for screen readers" />
      )}
    </View>
  );

  const renderLinkSection = () => (
    <View>
      <SectionTitle>Link details</SectionTitle>
      <Field label="URL *" value={linkUrl} onChangeText={setLinkUrl} placeholder="https://…" keyboardType="url" />
      {!!linkUrl && !isValidUrl(linkUrl) && <Text style={s.fieldError}>Enter a full URL including https://</Text>}
      <Field label="Link title" value={linkTitle} onChangeText={setLinkTitle} placeholder="e.g. Apply now on JobEng" />
      <Field label="Call-to-action text" value={linkCtaText} onChangeText={setLinkCtaText} placeholder="e.g. Apply Now, Learn More" />
    </View>
  );

  const renderSocialSection = () => (
    <View>
      <SectionTitle>Post content</SectionTitle>
      <Field
        label="Post text *"
        value={postText}
        onChangeText={setPostText}
        placeholder="Write your post…"
        multiline
        counter={charLimit ? `${postText.length}/${charLimit}` : `${postText.length} chars`}
      />
      {charLimit && postText.length > charLimit && (
        <Text style={s.fieldError}>Over the {charLimit}-character limit ({PLATFORMS.find((p) => p.charLimit === charLimit)?.id}).</Text>
      )}
      <TouchableOpacity style={s.aiInlineBtn} onPress={() => generateAiContent(`Write a social media post: ${postText || assetName || 'promote this campaign'}`)} disabled={aiGenerating}>
        {aiGenerating ? <ActivityIndicator size="small" color={C.accent} /> : <MaterialCommunityIcons name="brain" size={15} color={C.accent} />}
        <Text style={s.aiInlineText}>AI improve / write post</Text>
      </TouchableOpacity>
      {!!aiContent && assetType === 'social_post' && (
        <TouchableOpacity style={s.aiResultBox} onPress={() => setPostText(aiContent)}>
          <Text style={s.aiResultText} numberOfLines={6}>{aiContent}</Text>
          <Text style={s.aiUseText}>Tap to use as post text</Text>
        </TouchableOpacity>
      )}
      <Text style={s.fieldLabel}>Attached image (optional)</Text>
      {previewUri ? (
        <View style={s.filePreview}>
          <Image source={{ uri: previewUri }} style={s.previewImg} resizeMode="cover" onError={() => {}} />
          <View style={{ flex: 1, marginLeft: 12 }}>
            <Text style={s.fileName} numberOfLines={1}>{selectedFile?.name}</Text>
            <TouchableOpacity onPress={showImageOptions}><Text style={s.changeFile}>Change image</Text></TouchableOpacity>
          </View>
          <TouchableOpacity onPress={() => { setSelectedFile(null); setPreviewUri(null); }}>
            <Ionicons name="close-circle" size={22} color={C.danger} />
          </TouchableOpacity>
        </View>
      ) : (
        <TouchableOpacity style={[s.dropZone, { paddingVertical: 18 }]} onPress={showImageOptions} activeOpacity={0.7}>
          <MaterialCommunityIcons name="image-plus" size={26} color={C.purple} />
          <Text style={s.dropSub}>Add image</Text>
        </TouchableOpacity>
      )}
    </View>
  );

  const renderAiSection = () => (
    <View>
      <SectionTitle>AI content</SectionTitle>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ gap: 8, paddingVertical: 4 }}>
        {AI_QUICK_PROMPTS.map((p) => (
          <TouchableOpacity key={p} style={s.quickPrompt} onPress={() => generateAiContent(p)} disabled={aiGenerating}>
            <Text style={s.quickPromptText} numberOfLines={1}>{p}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
      <Field label="Prompt *" value={aiPrompt} onChangeText={setAiPrompt} placeholder="What should the AI write?" multiline />
      <TouchableOpacity style={[s.aiBtn, aiGenerating && { opacity: 0.6 }]} onPress={() => generateAiContent()} disabled={aiGenerating}>
        {aiGenerating ? <ActivityIndicator size="small" color="#fff" /> : <MaterialCommunityIcons name="auto-fix" size={17} color="#fff" />}
        <Text style={s.aiBtnText}>{aiGenerating ? 'Generating…' : 'Generate content'}</Text>
      </TouchableOpacity>
      {!!aiContent && (
        <View style={s.fieldWrap}>
          <View style={s.fieldLabelRow}>
            <Text style={s.fieldLabel}>Generated content (editable)</Text>
            <TouchableOpacity onPress={() => setAssetDescription(aiContent)}>
              <Text style={s.aiUseText}>Use as description</Text>
            </TouchableOpacity>
          </View>
          <TextInput
            style={[s.input, s.inputMultiline, { minHeight: 130 }]}
            value={aiContent}
            onChangeText={setAiContent}
            multiline
          />
        </View>
      )}
    </View>
  );

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  return (
    <SafeAreaView style={s.safe} edges={['top']}>
      {/* Header */}
      <View style={s.header}>
        <TouchableOpacity style={s.headerBtn} onPress={() => navigation.goBack()}>
          <Ionicons name="chevron-back" size={24} color="#fff" />
        </TouchableOpacity>
        <View style={{ flex: 1 }}>
          <Text style={s.headerTitle}>Add Campaign Asset</Text>
          <Text style={s.headerSub} numberOfLines={1}>{campaign?.name || 'Asset library'}</Text>
        </View>
        <DbBadge status={dbStatus} onPress={showDbDetail} />
      </View>

      <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <ScrollView style={s.body} keyboardShouldPersistTaps="handled" showsVerticalScrollIndicator={false}>
          {/* Type selector */}
          <SectionTitle>Asset type</SectionTitle>
          <View style={s.typeGrid}>
            {ASSET_TYPES.map((t) => (
              <TypeCard key={t.id} type={t} active={assetType === t.id} onPress={setAssetType} />
            ))}
          </View>

          {/* Core fields */}
          <SectionTitle>Details</SectionTitle>
          <Field label="Asset name *" value={assetName} onChangeText={setAssetName} placeholder="e.g. Spring launch hero image" />
          <Field label="Description" value={assetDescription} onChangeText={setAssetDescription} placeholder="What is this asset for?" multiline />

          {/* Type-specific */}
          {(assetType === 'image' || assetType === 'video' || assetType === 'document') && renderFileSection()}
          {assetType === 'link' && renderLinkSection()}
          {assetType === 'social_post' && renderSocialSection()}
          {assetType === 'ai_content' && renderAiSection()}

          {/* Platforms */}
          <SectionTitle>Target platforms</SectionTitle>
          <View style={s.platformGrid}>
            {PLATFORMS.map((p) => (
              <PlatformChip key={p.id} platform={p} active={targetPlatforms.includes(p.id)} onPress={togglePlatform} />
            ))}
          </View>

          {/* Tags */}
          <SectionTitle>Tags</SectionTitle>
          <View style={s.tagInputRow}>
            <TextInput
              style={[s.input, { flex: 1, marginBottom: 0 }]}
              value={tagInput}
              onChangeText={setTagInput}
              placeholder="Add tag and press +"
              placeholderTextColor="#9CA3AF"
              onSubmitEditing={addTag}
              returnKeyType="done"
            />
            <TouchableOpacity style={s.tagAddBtn} onPress={addTag}>
              <Ionicons name="add" size={20} color="#fff" />
            </TouchableOpacity>
          </View>
          {tags.length > 0 && (
            <View style={s.tagWrap}>
              {tags.map((t) => <TagPill key={t} tag={t} onRemove={removeTag} />)}
            </View>
          )}

          {/* Status */}
          <SectionTitle>Status</SectionTitle>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
            {STATUS_OPTIONS.map((st2) => (
              <Chip key={st2} label={st2.charAt(0).toUpperCase() + st2.slice(1)} active={status === st2} onPress={() => setStatus(st2)} />
            ))}
          </View>
          {status === 'scheduled' && (
            <Field label="Scheduled date" value={scheduledDate} onChangeText={setScheduledDate} placeholder="YYYY-MM-DD" />
          )}

          {/* Save */}
          {uploading ? (
            <View style={s.progressWrap}>
              <Text style={s.progressText}>Saving… {Math.floor(uploadProgress)}%</Text>
              <View style={s.progressTrack}>
                <View style={[s.progressFill, { width: `${uploadProgress}%` }]} />
              </View>
            </View>
          ) : (
            <TouchableOpacity style={s.saveBtn} onPress={handleSave} activeOpacity={0.85}>
              <Ionicons name="cloud-upload-outline" size={19} color="#fff" />
              <Text style={s.saveBtnText}>Save Asset</Text>
            </TouchableOpacity>
          )}
          <View style={{ height: 40 }} />
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

// =============================================================================
// STYLES
// =============================================================================

const s = StyleSheet.create({
  safe: { flex: 1, backgroundColor: C.primary },
  header: { flexDirection: 'row', alignItems: 'center', backgroundColor: C.primary, paddingHorizontal: 10, paddingVertical: 10, gap: 8 },
  headerBtn: { padding: 5 },
  headerTitle: { color: '#fff', fontSize: 17, fontWeight: '800' },
  headerSub: { color: 'rgba(255,255,255,0.65)', fontSize: 11, marginTop: 1 },

  dbBadge: { flexDirection: 'row', alignItems: 'center', gap: 5, backgroundColor: 'rgba(255,255,255,0.14)', borderRadius: 12, paddingHorizontal: 9, paddingVertical: 5 },
  dbDot: { width: 7, height: 7, borderRadius: 4 },
  dbText: { color: '#fff', fontSize: 10.5, fontWeight: '700' },

  body: { flex: 1, backgroundColor: C.bg, paddingHorizontal: 14 },

  sectionTitleRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, marginBottom: 8 },
  sectionTitle: { fontSize: 14.5, fontWeight: '800', color: C.text },

  typeGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  typeCard: { width: '31%', flexGrow: 1, alignItems: 'center', backgroundColor: C.card, borderRadius: 12, paddingVertical: 14, borderWidth: 1, borderColor: C.border },
  typeName: { fontSize: 11.5, fontWeight: '700', color: C.text, marginTop: 6 },

  fieldWrap: { marginBottom: 12 },
  fieldLabelRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 },
  fieldLabel: { fontSize: 12, fontWeight: '700', color: C.text, marginBottom: 6, marginTop: 4 },
  fieldCounter: { fontSize: 11, color: C.sub, fontWeight: '600' },
  fieldError: { fontSize: 11, color: C.danger, marginTop: -6, marginBottom: 8, fontWeight: '600' },
  input: { backgroundColor: C.card, borderWidth: 1, borderColor: C.border, borderRadius: 10, paddingHorizontal: 12, paddingVertical: Platform.OS === 'ios' ? 10 : 8, fontSize: 13.5, color: C.text, marginBottom: 4 },
  inputMultiline: { minHeight: 84, textAlignVertical: 'top' },

  specRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginBottom: 10 },
  specText: { fontSize: 10.5, color: C.sub, backgroundColor: '#EEF2F8', borderRadius: 7, paddingHorizontal: 8, paddingVertical: 4, fontWeight: '600' },

  dropZone: { alignItems: 'center', backgroundColor: C.card, borderRadius: 14, paddingVertical: 28, borderWidth: 1.5, borderColor: C.border, borderStyle: 'dashed', marginBottom: 8 },
  dropTitle: { fontSize: 13.5, fontWeight: '700', color: C.text, marginTop: 8 },
  dropSub: { fontSize: 11.5, color: C.sub, marginTop: 3 },

  filePreview: { flexDirection: 'row', alignItems: 'center', backgroundColor: C.card, borderRadius: 12, padding: 10, borderWidth: 1, borderColor: C.border, marginBottom: 8 },
  previewImg: { width: 64, height: 64, borderRadius: 10, backgroundColor: '#E8EDF6' },
  previewDoc: { alignItems: 'center', justifyContent: 'center' },
  fileName: { fontSize: 13, fontWeight: '700', color: C.text },
  fileMeta: { fontSize: 11, color: C.sub, marginTop: 2 },
  changeFile: { fontSize: 11.5, color: C.primary, fontWeight: '700', marginTop: 5 },

  platformGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  platformChip: { flexDirection: 'row', alignItems: 'center', gap: 5, backgroundColor: C.card, borderRadius: 10, paddingHorizontal: 10, paddingVertical: 7, borderWidth: 1, borderColor: C.border },
  platformText: { fontSize: 11.5, fontWeight: '700', color: C.text },

  chip: { paddingHorizontal: 13, paddingVertical: 7, borderRadius: 16, backgroundColor: C.card, borderWidth: 1, borderColor: C.border },
  chipActive: { backgroundColor: C.primary, borderColor: C.primary },
  chipText: { fontSize: 12.5, fontWeight: '600', color: C.sub },
  chipTextActive: { color: '#fff' },

  tagInputRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  tagAddBtn: { width: 40, height: 40, borderRadius: 10, backgroundColor: C.primary, alignItems: 'center', justifyContent: 'center' },
  tagWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 7, marginTop: 10 },
  tagPill: { flexDirection: 'row', alignItems: 'center', gap: 5, backgroundColor: '#EFF4FF', borderRadius: 14, paddingHorizontal: 10, paddingVertical: 5 },
  tagText: { fontSize: 11.5, fontWeight: '700', color: C.primary },

  quickPrompt: { backgroundColor: '#FCE7F3', borderRadius: 14, paddingHorizontal: 12, paddingVertical: 8, maxWidth: 260 },
  quickPromptText: { fontSize: 11.5, fontWeight: '700', color: C.accent },

  aiBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 7, backgroundColor: C.accent, borderRadius: 11, paddingVertical: 12, marginTop: 2 },
  aiBtnText: { color: '#fff', fontSize: 13.5, fontWeight: '800' },
  aiInlineBtn: { flexDirection: 'row', alignItems: 'center', gap: 5, alignSelf: 'flex-start', backgroundColor: '#FCE7F3', borderRadius: 9, paddingHorizontal: 10, paddingVertical: 6, marginBottom: 10 },
  aiInlineText: { fontSize: 11.5, fontWeight: '700', color: C.accent },
  aiResultBox: { backgroundColor: '#FDF2F8', borderRadius: 12, padding: 12, borderWidth: 1, borderColor: '#FBCFE8', marginBottom: 12 },
  aiResultText: { fontSize: 12.5, color: C.text, lineHeight: 18 },
  aiUseText: { fontSize: 11, fontWeight: '800', color: C.accent, marginTop: 6 },

  progressWrap: { marginTop: 16, backgroundColor: C.card, borderRadius: 12, padding: 14, borderWidth: 1, borderColor: C.border },
  progressText: { fontSize: 12.5, fontWeight: '800', color: C.text, marginBottom: 8, textAlign: 'center' },
  progressTrack: { height: 8, backgroundColor: '#E8EDF6', borderRadius: 4, overflow: 'hidden' },
  progressFill: { height: 8, backgroundColor: C.primaryLight, borderRadius: 4 },

  saveBtn: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, backgroundColor: C.success, borderRadius: 12, paddingVertical: 14, marginTop: 16 },
  saveBtnText: { color: '#fff', fontSize: 15, fontWeight: '800' },
});
